// Shared constants/helpers for the Subsidiyalar (Subsidies) admin feature.
// Backend contract: CONSOLE.md (repo root) — paths, MultiLang shape, scale/decimals math.
export const API_BASE = "api/v1/subsidies";

export const LANG_KEYS = ["uz_lat", "uz_cyr", "ru", "en", "kr"];

export const LANG_TABS = [
  {code: "uz_lat", tabLabel: "UZ"},
  {code: "uz_cyr", tabLabel: "UZ Krill"},
  {code: "ru", tabLabel: "RU"},
  {code: "en", tabLabel: "EN"},
  {code: "kr", tabLabel: "KR"},
];

export function emptyMultiLang() {
  return LANG_KEYS.reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, {});
}

// Merges a possibly-partial/absent MultiLang from the API onto the full 5-key shape.
export function fillMultiLang(source) {
  return LANG_KEYS.reduce((acc, key) => {
    acc[key] = source?.[key] || "";
    return acc;
  }, {});
}

// null when every language is empty — lets the console send `"unit": null` (CONSOLE.md §6)
// instead of an all-empty object when the admin left an optional multilang field untouched.
export function multiLangOrNull(value) {
  if (!value) return null;
  const hasAny = LANG_KEYS.some((key) => (value[key] || "").trim());
  return hasAny ? value : null;
}

export function displayLangText(multiLang) {
  if (!multiLang) return "";
  return multiLang.uz_lat || multiLang.uz_cyr || multiLang.ru || multiLang.en || multiLang.kr || "";
}

// Derives a backend-valid group_code (`^[a-z0-9_-]{1,32}$`) from an admin-typed
// group name (e.g. "Tasdiqlangan" -> "tasdiqlangan") so the "create new group" flow
// never sends a raw display string as the code.
export function slugifyGroupCode(text) {
  if (!text) return "";
  return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s_-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .slice(0, 32);
}

export const VALUE_TYPES = [
  {value: "AMOUNT", label: "Summa"},
  {value: "PERCENT", label: "Foiz"},
  {value: "TEXT", label: "Matn"},
];

export function valueTypeLabel(type) {
  return VALUE_TYPES.find((t) => t.value === type)?.label || type;
}

// CONSOLE.md §3 — "Birlik" dropdown offered instead of raw scale numbers.
export const SCALE_UNIT_OPTIONS = [
  {value: 0, label: "so'm"},
  {value: 3, label: "ming so'm"},
  {value: 6, label: "mln.so'm"},
  {value: 9, label: "mlrd.so'm"},
  {value: 12, label: "trln.so'm"},
];

export function scaleUnitLabel(scale) {
  return SCALE_UNIT_OPTIONS.find((o) => o.value === scale)?.label || `10^${scale} so'm`;
}

// CONSOLE.md §3.1 — 2^53, the largest integer float64 stores exactly; the server
// enforces the same limit, but the grid must catch it before ever sending a request.
export const MAX_SAFE_AMOUNT = 9007199254740992;

// Strips thousands separators (regular + non-breaking space) so raw digit strings
// coming out of an <input> (or pasted from Excel) can be parsed as a number.
export function stripThousandSeparators(str) {
  return (str ?? "").toString().replace(/[\s\u00a0']/g, "");
}

// Accepts admin-typed text ("447 541 500", "22,6", "-1 000") and returns a finite
// number or null if unparsable. Treats both ',' and '.' as decimal separators.
export function parseNumericInput(str) {
  if (str === null || str === undefined || str === "") return null;
  let cleaned = stripThousandSeparators(str);
  // Only a single ',' or '.' used as decimal marker — normalize ',' to '.'.
  const commaCount = (cleaned.match(/,/g) || []).length;
  const dotCount = (cleaned.match(/\./g) || []).length;
  if (commaCount === 1 && dotCount === 0) {
    cleaned = cleaned.replace(",", ".");
  } else {
    cleaned = cleaned.replace(/,/g, "");
  }
  if (cleaned === "" || cleaned === "-") return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : NaN;
}

// Inserts a thin space every 3 digits from the right, keeping a leading '-' and any
// decimal part untouched — CONSOLE.md §9.3 item 3 "minglik ajratgich yozayotganda".
export function groupThousands(str) {
  if (str === null || str === undefined) return "";
  const s = str.toString();
  const neg = s.startsWith("-") ? "-" : "";
  const body = neg ? s.slice(1) : s;
  const [intPart, fracPart] = body.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return neg + grouped + (fracPart !== undefined ? "." + fracPart : "");
}

// so'm -> scaled display string, e.g. (447541500000000, 9, 3) -> "447 541,500"
export function formatScaled(num, scale, decimals) {
  if (num === null || num === undefined || Number.isNaN(num)) return "";
  const scaled = num / Math.pow(10, scale || 0);
  const fixed = scaled.toFixed(decimals ?? 0);
  return groupThousands(fixed).replace(".", ",");
}

// CONSOLE.md §9.3 item 4 — grey out the last `scale` digits of the raw so'm integer
// so the admin can see exactly where the mlrd/mln/... boundary falls.
// Returns {head, tail} strings to render with different colors.
export function splitRazryad(num, scale) {
  const s = Math.trunc(Math.abs(num || 0)).toString();
  if (!scale || s.length <= scale) return {head: "", tail: s};
  return {head: s.slice(0, s.length - scale), tail: s.slice(s.length - scale)};
}

// CONSOLE.md §9.5 "Kalkulyator rejimi" (lite) — lets the admin type "447541.5 mlrd"
// (or "mln"/"ming"/"trln") into an AMOUNT cell and have it convert to raw so'm.
const UNIT_WORD_SCALE = {
  "so'm": 0, "som": 0, "sum": 0,
  "ming": 3, "min": 3,
  "mln": 6, "mln.so'm": 6,
  "mlrd": 9, "mlrd.so'm": 9,
  "trln": 12, "trln.so'm": 12,
};

export function parseAmountWithUnit(str) {
  if (!str) return null;
  const trimmed = str.toString().trim().toLowerCase();
  const m = /^(-?[\d\s.,]+)\s*([a-z']+)?$/.exec(trimmed);
  if (!m) return parseNumericInput(str);
  const num = parseNumericInput(m[1]);
  if (num === null || Number.isNaN(num)) return num;
  const unit = m[2];
  if (!unit) return num;
  const unitScale = UNIT_WORD_SCALE[unit];
  if (unitScale === undefined) return num;
  return num * Math.pow(10, unitScale);
}

// T/R raqami backenddan kelgan global `number` emas: har bir shox (bitta ota
// ostidagi qatorlar to'plami) o'z hisobini yuritadi — qatorlar `order` bo'yicha
// saralanadi va har bir qatlamda raqam qaytadan 1 dan boshlanadi.
function compareByOrder(a, b) {
  const diff = (Number(a.order) || 0) - (Number(b.order) || 0);
  return diff || (a.id - b.id);
}

// Daraxtni joyida (in-place) `order` bo'yicha saralaydi — qator obyektlarining
// identikligi saqlanadi, shuning uchun matritsadagi katak holati buzilmaydi.
export function sortTreeByOrder(nodes) {
  if (!Array.isArray(nodes)) return nodes;
  nodes.sort(compareByOrder);
  nodes.forEach((node) => sortTreeByOrder(node.children));
  return nodes;
}

// id -> shu qatorning o'z shoxidagi tartib raqami (1, 2, 3...). Chaqirilishidan
// oldin daraxt saralangan bo'lishi shart emas — nusxa ustida saralab hisoblaydi.
export function buildLayerNumbers(nodes, map = new Map()) {
  [...(nodes || [])].sort(compareByOrder).forEach((node, index) => {
    map.set(node.id, index + 1);
    if (node.children?.length) buildLayerNumbers(node.children, map);
  });
  return map;
}

// ---------------------------------------------------------------------------
// Davr (period) = yil + chorak — CONSOLE.md §2, CHANGELOG v2.
// `quarter` hech qachon null emas: 0 = yillik, 1..4 = choraklar. Query param'da
// ham, JSON body'da ham bir xil son.
// ---------------------------------------------------------------------------
export const ANNUAL_QUARTER = 0;

// CONSOLE.md §9.3 item 15 — har chorakning o'z rangi: banner va dropdown'da bir
// xil rang bo'lsa admin qaysi davrni tahrirlayotganini ko'z bilan ajratadi.
export const QUARTER_OPTIONS = [
  {value: 0, label: "Yillik", short: "Yillik", color: "#64748b"},
  {value: 1, label: "I chorak", short: "I ch.", color: "#2563eb"},
  {value: 2, label: "II chorak", short: "II ch.", color: "#16a34a"},
  {value: 3, label: "III chorak", short: "III ch.", color: "#f97316"},
  {value: 4, label: "IV chorak", short: "IV ch.", color: "#7c3aed"},
];

export function quarterLabel(quarter) {
  return QUARTER_OPTIONS.find((q) => q.value === Number(quarter))?.label || "Yillik";
}

export function quarterColor(quarter) {
  return QUARTER_OPTIONS.find((q) => q.value === Number(quarter))?.color || "#64748b";
}

export function normalizeQuarter(quarter) {
  const num = Number(quarter);
  return Number.isInteger(num) && num >= 0 && num <= 4 ? num : ANNUAL_QUARTER;
}

// Banner/sarlavha uchun: "2026-yil, I chorak" / "2026-yil, Yillik".
export function periodLabel(year, quarter) {
  if (!year) return "Davr tanlanmagan";
  return `${year}-yil, ${quarterLabel(quarter)}`;
}

// Fayl nomi suffiksi — yillikda suffiks yo'q (CHANGELOG §7).
export function periodFileSuffix(year, quarter) {
  const q = normalizeQuarter(quarter);
  return q === ANNUAL_QUARTER ? `${year}` : `${year}_Q${q}`;
}

// `GET /subsidies/admin/periods` javobini xavfsiz massivga aylantiradi.
export function normalizePeriods(data) {
  const list = Array.isArray(data?.periods) ? data.periods : [];
  return list
      .filter((p) => Number.isFinite(Number(p?.year)))
      .map((p) => ({
        year: Number(p.year),
        quarters: (Array.isArray(p.quarters) ? p.quarters : []).map(Number).filter((q) => q >= 1 && q <= 4),
        has_annual: !!p.has_annual,
      }));
}

// Konsol qiymatlarni faqat CHORAK kesimida boshqaradi — yillik (`quarter = 0`)
// yangi ma'lumot uchun ishlatilmaydi. Nol faqat eski ma'lumotni ko'rish/tahrirlash
// uchun, o'sha yilda haqiqatan yillik ma'lumot bo'lsa qo'shiladi.
export const QUARTER_ONLY_OPTIONS = QUARTER_OPTIONS.filter((q) => q.value !== ANNUAL_QUARTER);

// Yil uchun chorak variantlari: I..IV har doim; "Yillik" faqat eski ma'lumot bo'lsa.
export function quarterOptionsForYear(periods, year) {
  const legacyAnnual = (periods || []).find((p) => p.year === Number(year))?.has_annual;
  if (!legacyAnnual) return QUARTER_ONLY_OPTIONS;
  return [{...QUARTER_OPTIONS[0], label: "Yillik (eski)"}, ...QUARTER_ONLY_OPTIONS];
}

// Shu yilda ma'lumoti bor eng katta chorak; bo'lmasa I chorak. Yillik hech qachon
// default bo'lmaydi — faqat boshqa hech narsa bo'lmasa va u yagona bo'lsa.
export function pickDefaultQuarter(periods, year) {
  const period = (periods || []).find((p) => p.year === Number(year));
  if (period?.quarters?.length) return period.quarters[period.quarters.length - 1];
  if (period?.has_annual) return ANNUAL_QUARTER;
  return 1;
}

// Yil "mavjud" hisoblanadi, agar unga ustun biriktirilgan bo'lsa (`column.year`)
// yoki biror chorakda qiymat kiritilgan bo'lsa (`/admin/periods`).
export function yearsFromColumns(columns) {
  const years = (columns || [])
      .map((c) => c.year)
      .filter((y) => y !== null && y !== undefined)
      .map(Number)
      .filter(Number.isFinite);
  return Array.from(new Set(years));
}

export function mergeYears(...lists) {
  const set = new Set();
  lists.forEach((list) => (list || []).forEach((y) => {
    const num = Number(y);
    if (Number.isFinite(num) && num > 0) set.add(num);
  }));
  return Array.from(set).sort((a, b) => b - a);
}

// Dropdown'da "● ma'lumot bor" nuqtasini chizish uchun (CONSOLE.md §9.10).
export function periodHasData(periods, year, quarter) {
  const period = (periods || []).find((p) => p.year === Number(year));
  if (!period) return false;
  return normalizeQuarter(quarter) === ANNUAL_QUARTER
      ? period.has_annual
      : period.quarters.includes(normalizeQuarter(quarter));
}
